import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isUploadComplete, type ChatUpload } from "@altek/ui/models/chat/chat-types";
import { formatBytes } from "@altek/ui/lib/format/number";
import { waitForScan } from "@/lib/api/file-scan";
import { apiUpload } from "@/lib/api/upload";
import { apiErrorMessage } from "@/lib/api/error-messages";
import { chatFilesApi } from "../api/chat.api";
import { localId } from "../lib/chat-ids";
import { chatAttachmentPurposesQuery } from "../api/chat.queries";
import {
  ATTACHMENT_MAX_BYTES,
  ATTACHMENT_MAX_COUNT,
  CHAT_ATTACHMENT_PURPOSE,
} from "../api/chat.types";

/**
 * The three-step upload, as the composer sees it.
 *
 * §9: intent → PUT straight to storage → complete. The bytes never pass through the API, and the
 * fourth step (`POST /files/links`) is NOT ours: a chat attachment is bound by the send call
 * itself, through `attachmentFileIds`. So what this hook produces is a list of `fileId`s that the
 * message carries, and the upload has no meaning at all until a message is sent with them.
 *
 * THE LIMITS ARE READ, NOT WRITTEN DOWN. Size, count and accepted types come from
 * `GET /files/purposes` (§9: "hangi boyut sınırıyla yüklenebileceğini oradan öğren, sabit
 * kodlama"). The constants are the fallback for the moment before the catalogue answers — a
 * client that repeats the numbers starts refusing files the server would have taken, and without
 * the mime list it uploads 25 MB of a type the purpose refuses only to be told "no" at `complete`.
 *
 * WHY THE `File` OBJECTS ARE KEPT. Retry. `ChatUpload` is a view model — an id, a name, a size, a
 * percentage — and deliberately has no bytes in it (`chat-types.ts` explains why it is not a
 * `ChatFile` with a progress field). A retry that cannot reach the original `File` can only ask
 * the user to pick the same file again, which on a phone means finding it a second time.
 *
 * FAILURE KEEPS THE CHIP. An upload that vanishes on error takes with it the only evidence that
 * the user attached anything, and they send the message without noticing. The chip stays, carries
 * the reason in Turkish, and offers the retry.
 */

interface UploadRecord {
  file: File;
  fileId: string | null;
  controller: AbortController;
}

export interface ChatAttachments {
  uploads: ChatUpload[];
  /** The ids a send may carry: finished, error-free uploads only. */
  readyFileIds: string[];
  /** True while any chip is still uploading — a send must not silently drop those. */
  isUploading: boolean;
  attach: (files: File[]) => void;
  cancel: (id: string) => void;
  retry: (id: string) => void;
  /** After a successful send, the chips belong to the message and must leave the composer. */
  clear: () => void;
  /** A file the user chose that the rules refuse, phrased for a person. */
  rejection: string | null;
  dismissRejection: () => void;
}

/**
 * Does the slot accept this file's type?
 *
 * `image/*` is honoured because the catalogue writes families that way, and an EMPTY list means
 * "the slot did not say" — which is read as "allow" rather than "refuse everything". A client
 * that refuses on a missing catalogue is a composer that stops accepting files the moment the
 * shared endpoint is slow.
 */
function mimeAccepted(patterns: ReadonlyArray<string>, type: string): boolean {
  if (patterns.length === 0) return true;
  if (type === "") return true;
  return patterns.some((pattern) => {
    if (pattern === "*/*") return true;
    if (pattern.endsWith("/*")) return type.startsWith(`${pattern.slice(0, -1)}`);
    return pattern === type;
  });
}

export function useChatAttachments(options?: { enabled?: boolean }): ChatAttachments {
  const enabled = options?.enabled ?? true;
  const [uploads, setUploads] = useState<ChatUpload[]>([]);
  const [rejection, setRejection] = useState<string | null>(null);
  const records = useRef(new Map<string, UploadRecord>());

  /**
   * The list as the last render left it.
   *
   * `attach` has to know how many chips there already are BEFORE it mints ids, and reading that
   * inside a `setUploads` updater is what made this function impure: React 19's StrictMode
   * double-invokes an updater, so every attached file minted two ids, wrote two records and
   * started two upload pipelines — two intents, two 25 MB PUTs, two completes, one of them
   * orphaned because its id never reached `uploads` and `cancel`/`clear` could not find it.
   */
  const uploadsRef = useRef<ChatUpload[]>([]);
  useEffect(() => {
    uploadsRef.current = uploads;
  });

  // A composer that unmounts mid-upload — the dock closing, the thread changing — must not leave
  // a 25 MB PUT running against a surface that no longer exists to receive its result.
  useEffect(() => {
    const pending = records.current;
    return () => {
      for (const record of pending.values()) record.controller.abort();
      pending.clear();
    };
  }, []);

  const purposesQuery = useQuery(chatAttachmentPurposesQuery({ enabled }));

  /**
   * `Array.isArray` and not `?? []`: the catalogue is a shared endpoint this feature does not
   * own, and a body that is not the expected array would otherwise throw inside render and take
   * the composer — including somebody's unsent text — down with it. A missing catalogue costs
   * the exact limits; it must never cost the message.
   */
  const limits = useMemo(() => {
    const slot = (Array.isArray(purposesQuery.data) ? purposesQuery.data : []).find(
      (entry) => entry.key === CHAT_ATTACHMENT_PURPOSE,
    );
    return {
      maxBytes: slot?.maxBytes ?? ATTACHMENT_MAX_BYTES,
      maxCount: slot?.maxPerEntity ?? ATTACHMENT_MAX_COUNT,
      mimeTypes: slot?.mimeTypes ?? [],
    };
  }, [purposesQuery.data]);

  const patch = useCallback((id: string, change: Partial<ChatUpload>) => {
    setUploads((current) =>
      current.map((upload) => (upload.id === id ? { ...upload, ...change } : upload)),
    );
  }, []);

  const run = useCallback(
    async (id: string) => {
      const record = records.current.get(id);
      if (!record) return;

      const controller = new AbortController();
      record.controller = controller;

      try {
        const intent = await chatFilesApi.intent({
          filename: record.file.name,
          contentType: record.file.type || "application/octet-stream",
          byteSize: record.file.size,
        });

        await apiUpload({ url: intent.uploadUrl, headers: intent.headers }, record.file, {
          /**
           * Capped at 99 on purpose. The file is not attachable until `complete` answers, and a
           * bar resting at 100 beside a send button that still refuses the file reads as a stall.
           * `apiUpload` reports a real 100 when the bytes land; the cap is this screen's meaning
           * of "done", not the transfer's.
           */
          onProgress: (percent) => patch(id, { progress: Math.min(99, percent) }),
          signal: controller.signal,
        });

        await chatFilesApi.complete(intent.fileId);

        record.fileId = intent.fileId;
        // 100 only once `complete` has answered: until then the file exists in storage but is not
        // attachable, and a bar at 100 beside a send button that refuses the file is a lie.
        patch(id, { progress: 100, error: undefined, scan: "SCANNING" });

        /**
         * GÖNDERİM TARAMAYI BEKLİYOR (Sohbet 8, K3). `complete` dosyayı açmıyor: sunucu sohbet
         * gönderiminde CLEAN olmayan bir eki reddediyor. Çip «Taranıyor» kalıyor ve `isUploadComplete`
         * (kit 3.17) taranmamış bir eki hazır saymadığı için gönder düğmesi de kapalı kalıyor.
         * Zararlı ya da reddedilmiş dosya çipte HATA olarak duruyor — boş bir durum olarak değil.
         */
        const settled = await waitForScan(intent.fileId, {
          signal: controller.signal,
          onState: (state) => patch(id, { scan: state }),
        });
        patch(id, { scan: settled.status, error: undefined });
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;
        /**
         * Satırın üstündeki cümle UYGULAMANIN haritasından (K1 + K3): kotası dolan kişi burada
         * «Depolama alanınız doldu (1,9 GB / 2 GB)…», taramadan dönen ret ise «Dosyada zararlı
         * içerik bulundu…» görüyor. `apiErrorMessage` tanımadığı kodda zaten kit'in
         * `messageFor`'una düşüyor, yani kit'in bildiği hiçbir cümle kaybolmuyor — ama kit'in
         * TANIMADIĞI `QUOTA_EXCEEDED`, `FILE_INFECTED`, `FILE_ACTIVE_CONTENT:…` gibi kodlar
         * genel bir cümleye düşmüyor. Yükleme ve tarama reddinin okunduğu tek yer bu satır.
         */
        patch(id, { error: apiErrorMessage(error) });
      }
    },
    [patch],
  );

  /**
   * Decide first, then set state.
   *
   * Everything below the `setUploads` call used to live INSIDE its updater — minting ids, writing
   * to `records`, calling `setRejection` and scheduling `run`. All four are side effects, and an
   * updater React may call twice must have none. What reaches `setUploads` now is a pure append
   * of an array computed before the call.
   */
  const attach = useCallback(
    (files: File[]) => {
      const current = uploadsRef.current;
      const room = limits.maxCount - current.length;
      if (room <= 0) {
        setRejection(`Bir mesaja en çok ${limits.maxCount} dosya eklenebilir.`);
        return;
      }

      const tooBig = files.filter((file) => file.size > limits.maxBytes);
      const wrongType = files.filter(
        (file) => file.size <= limits.maxBytes && !mimeAccepted(limits.mimeTypes, file.type),
      );
      const allowed = files.filter(
        (file) => file.size <= limits.maxBytes && mimeAccepted(limits.mimeTypes, file.type),
      );
      const accepted = allowed.slice(0, room);

      // One sentence per reason, in the order the user is most likely to have hit them. Three
      // banners in a row would be dismissed without being read; "dosya eklenmedi" with no reason
      // sends the person back to the file picker to try the same file again.
      const reasons: string[] = [];
      if (tooBig.length > 0) {
        reasons.push(
          `${tooBig.map((file) => file.name).join(", ")}: dosya sınırı ${formatBytes(limits.maxBytes)}.`,
        );
      }
      if (wrongType.length > 0) {
        reasons.push(`${wrongType.map((file) => file.name).join(", ")}: bu dosya türü eklenemiyor.`);
      }
      if (accepted.length < allowed.length) {
        reasons.push(`Bir mesaja en çok ${limits.maxCount} dosya eklenebilir.`);
      }
      setRejection(reasons.length > 0 ? reasons.join(" ") : null);

      if (accepted.length === 0) return;

      const added: ChatUpload[] = accepted.map((file) => {
        const id = localId("up");
        records.current.set(id, { file, fileId: null, controller: new AbortController() });
        return { id, name: file.name, sizeBytes: file.size, progress: 0 };
      });

      uploadsRef.current = [...current, ...added];
      setUploads((previous) => [...previous, ...added]);
      for (const upload of added) void run(upload.id);
    },
    [limits, run],
  );

  const cancel = useCallback((id: string) => {
    records.current.get(id)?.controller.abort();
    records.current.delete(id);
    uploadsRef.current = uploadsRef.current.filter((upload) => upload.id !== id);
    setUploads((current) => current.filter((upload) => upload.id !== id));
  }, []);

  const retry = useCallback(
    (id: string) => {
      const record = records.current.get(id);
      if (!record) return;
      record.fileId = null;
      // Eski tarama durumu da siliniyor: yeniden denenen yükleme yeni bir dosya satırı doğuruyor.
      patch(id, { progress: 0, error: undefined, scan: undefined });
      void run(id);
    },
    [patch, run],
  );

  const clear = useCallback(() => {
    for (const record of records.current.values()) record.controller.abort();
    records.current.clear();
    uploadsRef.current = [];
    setUploads([]);
    setRejection(null);
  }, []);

  const readyFileIds = uploads
    .filter(isUploadComplete)
    .map((upload) => records.current.get(upload.id)?.fileId)
    .filter((fileId): fileId is string => typeof fileId === "string");

  const isUploading = uploads.some(
    (upload) => upload.error === undefined && !isUploadComplete(upload),
  );

  return {
    uploads,
    readyFileIds,
    isUploading,
    attach,
    cancel,
    retry,
    clear,
    rejection,
    dismissRejection: () => setRejection(null),
  };
}
